import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Mic, MicOff, PhoneOff, Volume2 } from "lucide-react";
import { Fragment } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { personaAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CallModal = ({ persona, isOpen, onClose }) => {
    const { currentWorkspace } = useAuth();
    const [callStatus, setCallStatus] = useState('initializing'); // initializing | connected | error | disconnected
    const [isMuted, setIsMuted] = useState(false);
    const [error, setError] = useState(null);
    // Live audio levels (0-100) for each party, so the modal can show who is
    // speaking — both indicators can light up at once (barge-in / overlap).
    const [botLevel, setBotLevel] = useState(0);
    const [userLevel, setUserLevel] = useState(0);
    const [muteBusy, setMuteBusy] = useState(false);
    // Browser autoplay policy can block the bot's audio (the element is created
    // async, after the click gesture is gone). When that happens we surface a
    // one-tap "enable audio" button that calls room.startAudio() from a gesture.
    const [needsAudioUnlock, setNeedsAudioUnlock] = useState(false);
    const [micError, setMicError] = useState(false);
    const roomRef = useRef(null);
    const audioElRef = useRef(null);

    // Keep refs current so the connect effect can use stable references without
    // listing onClose/currentWorkspace in its dep array — avoids re-spawning the
    // room on every parent re-render that recreates an inline onClose callback.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    const workspaceIdRef = useRef(currentWorkspace?.id);
    useEffect(() => { workspaceIdRef.current = currentWorkspace?.id; }, [currentWorkspace]);

    // A hard refresh / tab close won't run React cleanup, so disconnect on
    // pagehide too — this leaves the LiveKit room cleanly, which ends the bot
    // immediately instead of letting it linger until LiveKit's ICE timeout.
    useEffect(() => {
        const onPageHide = () => { roomRef.current?.disconnect(); };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, []);

    // Keyed on persona?.id (not the object) so a parent re-render that hands a
    // fresh persona reference doesn't tear down and re-spawn the live call.
    const personaId = persona?.id;
    useEffect(() => {
        if (!isOpen || !personaId) return;
        let cancelled = false;
        let room = null;
        let connected = false;
        let closeTimer = null;

        // Lightweight, prefixed logger so the whole call lifecycle is easy to
        // follow in the browser console.
        const log = (...a) => console.log('[persona-preview]', ...a);

        // Best-effort mic publish — the human can still HEAR the persona even if
        // publishing their mic fails ("engine not connected" timing, no device,
        // denied permission). Never let it tear the call down. Retries twice.
        const enableMic = async (attempt = 0) => {
            try {
                await room.localParticipant.setMicrophoneEnabled(true);
                if (!cancelled) setMicError(false);
                log('mic published');
            } catch (e) {
                log(`mic publish failed (attempt ${attempt + 1}):`, e?.message || e);
                if (!cancelled && attempt < 2) {
                    await new Promise((r) => setTimeout(r, 1000));
                    if (!cancelled) return enableMic(attempt + 1);
                }
                if (!cancelled) setMicError(true);
            }
        };

        const start = async () => {
            try {
                setCallStatus('initializing');
                setError(null);
                setNeedsAudioUnlock(false);
                setMicError(false);
                log('requesting preview for persona', personaId);
                const { room_url, token } = await personaAPI.startPreview(personaId, workspaceIdRef.current);
                if (cancelled) { log('cancelled after startPreview'); return; }
                log('preview room:', room_url);

                room = new Room();
                roomRef.current = room;

                // Play the persona bot's audio + drive the volume bar.
                room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
                    log('track subscribed:', track.kind, 'from', participant?.identity);
                    if (track.kind === Track.Kind.Audio) {
                        const el = track.attach();
                        el.autoplay = true;
                        el.muted = false;
                        el.volume = 1;
                        el.setAttribute('playsinline', 'true');
                        audioElRef.current = el;
                        document.body.appendChild(el);
                        // Attempt playback. If the browser blocks it (autoplay
                        // policy — the gesture window from the mic click is gone
                        // by the time the track arrives), flip on the unlock UI
                        // directly off the play() result rather than relying on
                        // the canPlaybackAudio event, which can lag.
                        el.play()
                            .then(() => { log('bot audio element playing'); if (!cancelled) setNeedsAudioUnlock(false); })
                            .catch((e) => { log('autoplay blocked — showing unlock button:', e?.message || e); if (!cancelled) setNeedsAudioUnlock(true); });
                    }
                });
                room.on(RoomEvent.ConnectionStateChanged, (state) => log('connection state:', state));
                room.on(RoomEvent.ParticipantConnected, (p) => log('participant joined:', p?.identity));
                room.on(RoomEvent.ParticipantDisconnected, (p) => log('participant left:', p?.identity));
                room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
                    log('audio playback allowed:', room.canPlaybackAudio);
                    if (!cancelled) setNeedsAudioUnlock(!room.canPlaybackAudio);
                });
                room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                    // Split the active-speaker set into "me" (local mic) vs the
                    // persona bot (any remote), so both indicators can move
                    // independently — including when we talk over each other.
                    const localId = room.localParticipant.identity;
                    const bot = speakers.find((p) => p.identity !== localId);
                    const me = speakers.find((p) => p.identity === localId);
                    setBotLevel(bot ? Math.min(100, Math.round((bot.audioLevel || 0) * 400)) : 0);
                    setUserLevel(me ? Math.min(100, Math.round((me.audioLevel || 0) * 400)) : 0);
                });
                // Only treat a disconnect as "call ended" once we were actually
                // connected — a disconnect during teardown/StrictMode shouldn't
                // flash "Call ended" and close the modal.
                room.on(RoomEvent.Disconnected, (reason) => {
                    log('disconnected. reason:', reason, '(connected was', connected + ')');
                    if (cancelled || !connected) return;
                    setCallStatus('disconnected');
                    closeTimer = setTimeout(() => onCloseRef.current(), 800);
                });

                log('connecting to room…');
                await room.connect(room_url, token);
                if (cancelled) { log('cancelled after connect'); room.disconnect(); return; }
                connected = true;
                log('room connected; participants:', room.remoteParticipants?.size ?? 0);

                // Mark connected as soon as the room is up — HEARING the persona
                // must not depend on the mic publish below.
                setCallStatus('connected');

                // Unlock audio playback (autoplay policy). Falls back to the
                // "Tap to hear" button via AudioPlaybackStatusChanged.
                try { await room.startAudio(); log('startAudio ok'); } catch (e) { log('startAudio threw:', e?.message || e); }
                setNeedsAudioUnlock(!room.canPlaybackAudio);

                // Publish the mic so the human can talk — best-effort, non-fatal.
                enableMic();
            } catch (err) {
                log('FAILED:', err?.message || err);
                console.error('Persona preview failed:', err);
                if (cancelled) return;
                setError(err?.response?.data?.error || err.message || 'Failed to start preview');
                setCallStatus('error');
                closeTimer = setTimeout(() => onCloseRef.current(), 3000);
            }
        };

        // Defer the start one tick so React 18 StrictMode's mount→unmount→mount
        // in dev fires only ONE session (the first run is cancelled before this
        // timer fires) — avoids a double connect/disconnect and orphan rooms.
        const startTimer = setTimeout(start, 80);

        return () => {
            cancelled = true;
            clearTimeout(startTimer);
            if (closeTimer) clearTimeout(closeTimer);
            if (room) { room.disconnect(); }
            roomRef.current = null;
            const el = audioElRef.current;
            if (el) { el.remove(); audioElRef.current = null; }
        };
    }, [isOpen, personaId]);

    const handleClose = () => {
        if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; }
        onClose();
    };

    const handleToggleMute = async () => {
        const room = roomRef.current;
        if (!room || muteBusy) return;       // ignore rapid double-clicks mid-toggle
        const next = !isMuted;
        setMuteBusy(true);
        try {
            // setMicrophoneEnabled(false) unpublishes/disables the mic track, so
            // the persona receives no audio and our "You" level drops to 0.
            await room.localParticipant.setMicrophoneEnabled(!next);
            setIsMuted(next);
            if (next) setUserLevel(0);
            console.log('[persona-preview] mic', next ? 'muted' : 'unmuted',
                '— track enabled:', room.localParticipant.isMicrophoneEnabled);
        } catch (e) {
            console.log('[persona-preview] mute toggle failed:', e?.message || e);
        } finally {
            setMuteBusy(false);
        }
    };

    const handleEnableAudio = async () => {
        const room = roomRef.current;
        if (!room) return;
        // This runs inside a real user gesture (the button click), which is what
        // the browser requires to start audio playback.
        try { await room.startAudio(); } catch (_) {}
        try {
            if (audioElRef.current) await audioElRef.current.play();
            setNeedsAudioUnlock(false);
        } catch (_) {
            setNeedsAudioUnlock(true);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={handleClose}>
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>
                    <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                        <div className="inline-block w-full max-w-md p-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-card shadow-xl rounded-2xl">
                            <div className="absolute top-4 right-4">
                                <button onClick={handleClose} className="text-muted-foreground hover:text-muted-foreground">
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                            <Dialog.Title as="h3" className="text-lg font-medium text-center text-foreground">
                                {persona?.name || 'Voice Call'}
                            </Dialog.Title>
                            <div className="mt-2">
                                <p className="text-sm text-center text-muted-foreground">
                                    {callStatus === 'initializing' && 'Connecting…'}
                                    {callStatus === 'connected' && 'Connected — start talking'}
                                    {callStatus === 'disconnected' && 'Call ended'}
                                    {callStatus === 'error' && (error || 'Call failed')}
                                </p>
                            </div>
                            {/* Speaking indicators — You + persona, each lit by its
                                own live audio level so overlap (barge-in) is visible. */}
                            <div className="flex justify-center items-start gap-10 mt-8 mb-8">
                                {/* You */}
                                <div className="flex flex-col items-center gap-2 w-24">
                                    <div className="relative w-20 h-20 flex items-center justify-center">
                                        <span
                                            className="absolute inset-0 rounded-full bg-blue-400/30 transition-all duration-100"
                                            style={{ transform: `scale(${1 + Math.min(userLevel, 60) / 110})`, opacity: !isMuted && userLevel > 8 ? 1 : 0 }}
                                        />
                                        <div className={`relative w-20 h-20 rounded-full flex items-center justify-center ring-2 transition-colors ${!isMuted && userLevel > 8 ? 'ring-blue-400' : 'ring-transparent'} ${isMuted ? 'bg-red-100' : 'bg-muted'}`}>
                                            {isMuted
                                                ? <MicOff className="h-8 w-8 text-red-500" />
                                                : <Mic className={`h-8 w-8 ${userLevel > 8 ? 'text-blue-500' : 'text-muted-foreground'}`} />}
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium text-foreground">You</span>
                                    <span className="text-xs h-4 text-muted-foreground">
                                        {isMuted ? 'Muted' : (userLevel > 8 ? 'Speaking…' : '')}
                                    </span>
                                </div>
                                {/* Persona */}
                                <div className="flex flex-col items-center gap-2 w-24">
                                    <div className="relative w-20 h-20 flex items-center justify-center">
                                        <span
                                            className="absolute inset-0 rounded-full bg-green-400/30 transition-all duration-100"
                                            style={{ transform: `scale(${1 + Math.min(botLevel, 60) / 110})`, opacity: botLevel > 8 ? 1 : 0 }}
                                        />
                                        <div className={`relative w-20 h-20 rounded-full flex items-center justify-center ring-2 transition-colors ${botLevel > 8 ? 'ring-green-400' : 'ring-transparent'} bg-muted`}>
                                            <span className="text-2xl font-semibold text-foreground">
                                                {(persona?.name || 'P').charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium text-foreground truncate max-w-[6rem]">{persona?.name || 'Persona'}</span>
                                    <span className="text-xs h-4 text-muted-foreground">
                                        {botLevel > 8 ? 'Speaking…' : ''}
                                    </span>
                                </div>
                            </div>

                            {/* Autoplay was blocked — one tap to let the persona's voice through */}
                            {callStatus === 'connected' && needsAudioUnlock && (
                                <div className="mb-6 flex justify-center">
                                    <button
                                        onClick={handleEnableAudio}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary/90"
                                    >
                                        <Volume2 className="h-4 w-4" />
                                        Tap to hear {persona?.name || 'the persona'}
                                    </button>
                                </div>
                            )}

                            {callStatus === 'connected' && (
                                <div className="flex justify-center items-center space-x-6">
                                    <button
                                        onClick={handleToggleMute}
                                        disabled={muteBusy}
                                        title={isMuted ? 'Unmute' : 'Mute'}
                                        className={`p-3 rounded-full transition-colors disabled:opacity-50 ${isMuted ? 'bg-red-100 text-red-500' : 'bg-muted text-muted-foreground'} hover:bg-opacity-80`}
                                    >
                                        {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                                    </button>
                                    <button
                                        onClick={handleClose}
                                        title="End call"
                                        className="p-4 rounded-full bg-red-500 text-white hover:bg-red-600 shadow"
                                    >
                                        <PhoneOff className="h-6 w-6" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

export default CallModal;
